import { produceReelClips } from '@src/services/reelClipProducer/produceReelClips';
import { useStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

import type { ReelOutline, ReelProducerConfig } from './types';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

const logger = LogManager.getLogger('ReelProducer:useReelClipGeneration');

type UseReelClipGenerationResult = {
  results: ReelClipResults | null;
  isGenerating: boolean;
  error: string | null;
  generate: (outline: ReelOutline, config: ReelProducerConfig) => Promise<void>;
};

export function useReelClipGeneration(): UseReelClipGenerationResult {
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();
  const transcriptionStore = useTranscription();

  const [results, setResults] = useState<ReelClipResults | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation token: incremented on each generate() call so that stale
  // callbacks from a previous (still-running) generation are ignored.
  const generationTokenRef = useRef(0);

  const generate = useCallback(
    async (outline: ReelOutline, config: ReelProducerConfig) => {
      const token = ++generationTokenRef.current;

      setResults(null);
      setIsGenerating(true);
      setError(null);

      try {
        const transcriptElements =
          transcriptionStore.getFinalTranscriptionElements();
        const videoDuration = videoCreator.duration;

        const originalVideo = videoCreator.story?.originalVideo;
        if (!originalVideo) {
          throw new Error('No original video found on current story');
        }

        logger.info('Starting reel clip generation', {
          outlineTitle: outline.title,
          chapters: outline.chapters.length,
          totalTargetDuration: outline.totalTargetDuration,
        });

        // Prefer the original full transcription over the editor's current (possibly trimmed) transcript
        const originalTranscription = transcriptionStore.getOriginalTranscription();
        const fullElements = originalTranscription?.elements ?? transcriptElements;

        const clipResults = await produceReelClips({
          outline,
          transcriptElements: fullElements,
          originalVideo,
          videoDuration,
          rootStore,
          videoCreator,
          allSpeakers: config.allSpeakers,
          selectedSpeakers: config.selectedSpeakers,
          selectedGoal: config.selectedGoal,
          selectedAudiences: config.selectedAudiences,
          videoTemplateId: config.videoTemplateId,
          onPartialResults: (partial) => {
            // Ignore results from a superseded generation
            if (token !== generationTokenRef.current) return;
            setResults(new Map(partial));
          },
        });

        // Ignore final results from a superseded generation
        if (token !== generationTokenRef.current) return;

        setResults(clipResults);
        logger.info('Reel clip generation complete', {
          beatCount: clipResults.size,
        });
      } catch (err) {
        if (token !== generationTokenRef.current) return;
        const errMsg = err instanceof Error ? err.message : String(err);
        logger.error(`Reel clip generation failed: ${errMsg}`, err);
        setError(errMsg);
      } finally {
        if (token === generationTokenRef.current) {
          setIsGenerating(false);
        }
      }
    },
    [rootStore, videoCreator, transcriptionStore],
  );

  return { results, isGenerating, error, generate };
}
